// Thin route wrapper for canonical collection pages.

import React, { useMemo } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import {
  formatBlogTag,
  getCollectionById,
  getCollectionByRoute,
  isValidBlogTag,
  type BlogTag,
  type Collection,
} from '@airhead-games/shared/content';
import CollectionTemplate from '@/content/rendering/templates/CollectionTemplate';

interface CollectionRouteProps {
  collectionId?: string;
  fallbackPath?: string;
}

const buildBlogTagCollection = (
  baseCollection: Collection,
  tag: BlogTag
): Collection => ({
  ...baseCollection,
  route: `/blog/category/${tag}`,
  title: 'Blog',
  description: `Read our latest posts about ${formatBlogTag(tag).toLowerCase()}.`,
  seo: {
    ...baseCollection.seo,
    title: `${formatBlogTag(tag)} - Blog`,
    description: `Read our latest posts about ${formatBlogTag(tag).toLowerCase()}.`,
  },
  hero: {
    ...baseCollection.hero,
    title: [{ type: 'text', text: 'Blog' }],
  },
  sections: [
    {
      id: 'posts',
      title: [{ type: 'text', text: `Posts for ${formatBlogTag(tag)}` }],
      widgets: [
        {
          type: 'documentList',
          source: {
            type: 'query',
            kind: 'blog',
            tags: [tag],
            sortBy: 'publishedAt-desc',
          },
          layout: 'featured-grid',
        },
      ],
    },
  ],
});

const CollectionRoute: React.FC<CollectionRouteProps> = ({
  collectionId,
  fallbackPath = '/discover',
}) => {
  const location = useLocation();
  const { tag } = useParams<{ tag?: string }>();

  const collection = useMemo(() => {
    const baseCollection = collectionId
      ? getCollectionById(collectionId)
      : getCollectionByRoute(location.pathname);

    if (!baseCollection) {
      return undefined;
    }

    if (collectionId === 'collection:blog' && tag) {
      return isValidBlogTag(tag)
        ? buildBlogTagCollection(baseCollection, tag)
        : undefined;
    }

    return baseCollection;
  }, [collectionId, location.pathname, tag]);

  if (!collection) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <CollectionTemplate collection={collection} />;
};

export default CollectionRoute;
